import { toaster } from '@optiaxiom/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { SpecializedAgentExecution } from '@frontend/types';

import { resumeSpecializedAgentExecution } from '../services/specialized-agent';

type UseResumeSpecializedAgentExecutionOptions = {
  onSuccess?: (data: SpecializedAgentExecution) => void;
  onError?: (error: Error) => void;
};

export function useResumeSpecializedAgentExecution(
  options?: UseResumeSpecializedAgentExecutionOptions
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      instanceId,
      executionId,
    }: {
      instanceId: string;
      executionId: string;
    }) => {
      return resumeSpecializedAgentExecution(instanceId, executionId)();
    },
    onSuccess: (data, variables) => {
      const queryKey = [
        'opal:specialized-agent-execution',
        variables.instanceId,
        variables.executionId,
      ];

      // Update the cache with the new execution data
      // This changes status from FAILED to STARTED
      queryClient.setQueryData(queryKey, data);

      // Force refetch to restart polling (refetchInterval will now see isStreaming = true)
      queryClient.refetchQueries({ queryKey });

      // Also invalidate the executions list to refresh it
      queryClient.invalidateQueries({
        queryKey: ['specializedAgentExecutions'],
      });

      toaster.create('Execution resumed successfully', { type: 'success' });

      options?.onSuccess?.(data);
    },
    onError: (error: Error) => {
      toaster.create(`Failed to resume execution: ${error.message}`, { type: 'danger' });

      options?.onError?.(error);
    },
  });
}
